import { API } from "../../api";
import {
  mapStaffMarketplaceCategory,
  mapStaffMarketplaceProduct,
  mapStaffMarketplaceOrder,
  mapStaffMarketplaceDashboard,
} from "../../../utils/staffMarketplaceAdapter";

function transformListResponse(responseData, mapItem) {
  const data = responseData?.data;
  return {
    items: (data?.items || []).map(mapItem).filter(Boolean),
    pagination: data?.pagination || {},
  };
}

const ORDER_ACTIONS = {
  awaitingConfirmation: "awaiting-confirmation",
  confirm: "confirm",
  startProcessing: "start-processing",
  readyForPickup: "ready-for-pickup",
  ship: "ship",
  deliver: "deliver",
  complete: "complete",
  decline: "decline",
  cancel: "cancel",
};

export const staffMarketplaceApi = API.injectEndpoints({
  endpoints: (builder) => ({
    getStaffMarketplaceDashboard: builder.query({
      query: () => ({
        url: "/cooperatives/marketplace/dashboard/summary",
        method: "GET",
      }),
      transformResponse: (responseData) => mapStaffMarketplaceDashboard(responseData?.data),
      providesTags: [{ type: "StaffMarketplaceDashboard", id: "SUMMARY" }],
    }),

    listStaffMarketplaceCategories: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/marketplace/categories",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) =>
        transformListResponse(responseData, mapStaffMarketplaceCategory),
      providesTags: [{ type: "StaffMarketplaceCategories", id: "LIST" }],
    }),
    createStaffMarketplaceCategory: builder.mutation({
      query: (body) => ({
        url: "/cooperatives/marketplace/categories",
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => mapStaffMarketplaceCategory(responseData?.data),
      invalidatesTags: [{ type: "StaffMarketplaceCategories", id: "LIST" }],
    }),
    updateStaffMarketplaceCategory: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/marketplace/categories/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (responseData) => mapStaffMarketplaceCategory(responseData?.data),
      invalidatesTags: [{ type: "StaffMarketplaceCategories", id: "LIST" }],
    }),
    activateStaffMarketplaceCategory: builder.mutation({
      query: (id) => ({
        url: `/cooperatives/marketplace/categories/${id}/activate`,
        method: "POST",
      }),
      transformResponse: (responseData) => mapStaffMarketplaceCategory(responseData?.data),
      invalidatesTags: [{ type: "StaffMarketplaceCategories", id: "LIST" }],
    }),
    deactivateStaffMarketplaceCategory: builder.mutation({
      query: (id) => ({
        url: `/cooperatives/marketplace/categories/${id}/deactivate`,
        method: "POST",
      }),
      transformResponse: (responseData) => mapStaffMarketplaceCategory(responseData?.data),
      invalidatesTags: [{ type: "StaffMarketplaceCategories", id: "LIST" }],
    }),

    listStaffMarketplaceProducts: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/marketplace/products",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) =>
        transformListResponse(responseData, mapStaffMarketplaceProduct),
      providesTags: (result) =>
        result?.items?.length
          ? [
              ...result.items.map((item) => ({
                type: "StaffMarketplaceProducts",
                id: item.slug || item.id,
              })),
              { type: "StaffMarketplaceProducts", id: "LIST" },
            ]
          : [{ type: "StaffMarketplaceProducts", id: "LIST" }],
    }),
    getStaffMarketplaceProduct: builder.query({
      query: (productKey) => ({
        url: `/cooperatives/marketplace/products/${encodeURIComponent(productKey)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapStaffMarketplaceProduct(responseData?.data),
      providesTags: (_r, _e, productKey) => [{ type: "StaffMarketplaceProducts", id: productKey }],
    }),
    getStaffMarketplaceProductPreview: builder.query({
      query: (productKey) => ({
        url: `/cooperatives/marketplace/products/${encodeURIComponent(productKey)}/preview`,
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data,
      providesTags: (_r, _e, productKey) => [
        { type: "StaffMarketplaceProducts", id: `preview-${productKey}` },
      ],
    }),
    createStaffMarketplaceProduct: builder.mutation({
      query: (body) => ({
        url: "/cooperatives/marketplace/products",
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => mapStaffMarketplaceProduct(responseData?.data),
      invalidatesTags: [
        { type: "StaffMarketplaceProducts", id: "LIST" },
        { type: "StaffMarketplaceDashboard", id: "SUMMARY" },
      ],
    }),
    updateStaffMarketplaceProduct: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/marketplace/products/${encodeURIComponent(id)}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (responseData) => mapStaffMarketplaceProduct(responseData?.data),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "StaffMarketplaceProducts", id },
        { type: "StaffMarketplaceProducts", id: "LIST" },
      ],
    }),
    staffMarketplaceProductLifecycle: builder.mutation({
      query: ({ id, action }) => ({
        url: `/cooperatives/marketplace/products/${encodeURIComponent(id)}/${action}`,
        method: "POST",
      }),
      transformResponse: (responseData) => mapStaffMarketplaceProduct(responseData?.data),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "StaffMarketplaceProducts", id },
        { type: "StaffMarketplaceProducts", id: "LIST" },
        { type: "StaffMarketplaceDashboard", id: "SUMMARY" },
      ],
    }),
    adjustStaffMarketplaceInventory: builder.mutation({
      query: ({ id, body }) => ({
        url: `/cooperatives/marketplace/products/${encodeURIComponent(id)}/inventory/adjust`,
        method: "POST",
        body,
      }),
      transformResponse: (responseData) => responseData?.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: "StaffMarketplaceProducts", id },
        { type: "StaffMarketplaceProducts", id: "LIST" },
        { type: "StaffMarketplaceDashboard", id: "SUMMARY" },
      ],
    }),
    listStaffMarketplaceInventoryHistory: builder.query({
      query: (productKey) => ({
        url: `/cooperatives/marketplace/products/${encodeURIComponent(productKey)}/inventory/history`,
        method: "GET",
      }),
      transformResponse: (responseData) => responseData?.data?.items || responseData?.data || [],
      providesTags: (_r, _e, productKey) => [
        { type: "StaffMarketplaceProducts", id: `inv-${productKey}` },
      ],
    }),

    listStaffMarketplaceOrders: builder.query({
      query: (params = {}) => ({
        url: "/cooperatives/marketplace/orders",
        method: "GET",
        params,
      }),
      transformResponse: (responseData) =>
        transformListResponse(responseData, mapStaffMarketplaceOrder),
      providesTags: [{ type: "StaffMarketplaceOrders", id: "LIST" }],
    }),
    getStaffMarketplaceOrder: builder.query({
      query: (orderNumber) => ({
        url: `/cooperatives/marketplace/orders/${encodeURIComponent(orderNumber)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => mapStaffMarketplaceOrder(responseData?.data),
      providesTags: (_r, _e, id) => [{ type: "StaffMarketplaceOrders", id }],
    }),
    staffMarketplaceOrderLifecycle: builder.mutation({
      query: ({ orderNumber, action, body }) => ({
        url: `/cooperatives/marketplace/orders/${encodeURIComponent(orderNumber)}/${action}`,
        method: "POST",
        body: body || {},
      }),
      transformResponse: (responseData) => mapStaffMarketplaceOrder(responseData?.data),
      invalidatesTags: (_r, _e, { orderNumber }) => [
        { type: "StaffMarketplaceOrders", id: "LIST" },
        { type: "StaffMarketplaceOrders", id: orderNumber },
        { type: "StaffMarketplaceProducts", id: "LIST" },
        { type: "StaffMarketplaceDashboard", id: "SUMMARY" },
      ],
    }),
    addStaffMarketplaceOrderNote: builder.mutation({
      query: ({ orderNumber, note }) => ({
        url: `/cooperatives/marketplace/orders/${encodeURIComponent(orderNumber)}/notes`,
        method: "POST",
        body: { note },
      }),
      transformResponse: (responseData) => mapStaffMarketplaceOrder(responseData?.data),
      invalidatesTags: (_r, _e, { orderNumber }) => [
        { type: "StaffMarketplaceOrders", id: orderNumber },
      ],
    }),
    assignStaffMarketplaceOrder: builder.mutation({
      query: ({ orderNumber, assigned_staff_id }) => ({
        url: `/cooperatives/marketplace/orders/${encodeURIComponent(orderNumber)}/assign`,
        method: "POST",
        body: { assigned_staff_id },
      }),
      transformResponse: (responseData) => mapStaffMarketplaceOrder(responseData?.data),
      invalidatesTags: (_r, _e, { orderNumber }) => [
        { type: "StaffMarketplaceOrders", id: "LIST" },
        { type: "StaffMarketplaceOrders", id: orderNumber },
      ],
    }),
  }),
});

export const STAFF_MARKETPLACE_ORDER_ACTIONS = ORDER_ACTIONS;

export const {
  useGetStaffMarketplaceDashboardQuery,
  useListStaffMarketplaceCategoriesQuery,
  useCreateStaffMarketplaceCategoryMutation,
  useUpdateStaffMarketplaceCategoryMutation,
  useActivateStaffMarketplaceCategoryMutation,
  useDeactivateStaffMarketplaceCategoryMutation,
  useListStaffMarketplaceProductsQuery,
  useGetStaffMarketplaceProductQuery,
  useGetStaffMarketplaceProductPreviewQuery,
  useCreateStaffMarketplaceProductMutation,
  useUpdateStaffMarketplaceProductMutation,
  useStaffMarketplaceProductLifecycleMutation,
  useAdjustStaffMarketplaceInventoryMutation,
  useListStaffMarketplaceInventoryHistoryQuery,
  useListStaffMarketplaceOrdersQuery,
  useGetStaffMarketplaceOrderQuery,
  useStaffMarketplaceOrderLifecycleMutation,
  useAddStaffMarketplaceOrderNoteMutation,
  useAssignStaffMarketplaceOrderMutation,
} = staffMarketplaceApi;
